import React from 'react';
import './style.scss';
import { LabelValueUnitSelector } from 'views/templates/module/unitSelector';
import { Spinbox } from 'views/mutual/spinbox';
import { formatPriceWithFallback, multiplyNullable } from '../../calculation';
import { FontAlign, FontColor, FontSize, FontWeight } from '../../enums';
import { LotInputCommonDescriptions } from '../common';
import { useLotInputAmountControl } from '../../hooks';
import type { DomesticRealEstateSymbolDetailInfo } from 'views/utils/graphql/graphql';

type ITExplanationLotInputProps = {
  title: string;
  realEstateBeneficiaryRightPrice: number | null;
  realEstatePriceEquivalent: number | null;
  consumptionTaxAmount: number | null;
  repairReserveFundEquivalent: number | null;
  requiredWorkingCapitalReserveEquivalent: number | null;
  disabled: boolean | undefined;
  error: string | undefined;
  onChange: ((value: string) => void) | undefined;
  value: number | null | undefined;
  symbolDetail: DomesticRealEstateSymbolDetailInfo | null | undefined;
  showNotice: boolean;
};

type ITExplanationOverviewRowProps = {
  label: string;
  value: string;
  size: (typeof FontSize)[keyof typeof FontSize];
  weight: (typeof FontWeight)[keyof typeof FontWeight];
  color: (typeof FontColor)[keyof typeof FontColor];
  align: (typeof FontAlign)[keyof typeof FontAlign];
};

const ITExplanationOverviewRow = ({ label, value, size, weight, color, align }: ITExplanationOverviewRowProps) => (
  <div className={`itExplanationOverviewRow size_${size} weight_${weight} color_${color} align_${align}`}>
    <div className="itExplanationOverviewRow_label">{label}</div>
    <div className="itExplanationOverviewRow_value">{value}</div>
  </div>
);

export const ITExplanationLotInputBody = (props: ITExplanationLotInputProps) => {
  const { amountProps } = useLotInputAmountControl({
    disabled: props.disabled,
    error: props.error,
    onChange: props.onChange,
    currentAmount: props.value,
    amountOptionsRaw: props.symbolDetail?.AmountOptions ?? [],
    step: props.symbolDetail?.SalesUnitCtrl!,
    maxQuantity: props.symbolDetail?.MaxInvestmentQuantity!,
    unit: '個',
  });
  const amount = amountProps.showManualQuantity
    ? amountProps.spinboxProps.value
    : Number(amountProps.unitSelectorProps.selectedOption);

  return (
    <div className="lotInput">
      <div className="lotInput_header">{props.title}</div>
      <div className="lotInput_body">
        <div className="overview">
          <div className="overview_inputField">
            <span>{'申込個数：'}</span>
            <LabelValueUnitSelector {...amountProps.unitSelectorProps} />
          </div>
          <div className="overview_inputField">
            {amountProps.showManualQuantity && (
              <>
                <span>{'手入力（個）：'}</span>
                <Spinbox {...amountProps.spinboxProps} />
              </>
            )}
          </div>
          <ITExplanationOverviewRow
            label="不動産受益権価格："
            value={formatPriceWithFallback(multiplyNullable(props.realEstateBeneficiaryRightPrice, amount))}
            weight={FontWeight.bold}
            color={FontColor.red}
            align={FontAlign.right}
            size={FontSize.medium}
          />
          <ITExplanationOverviewRow
            label={`（内訳）`}
            value={''}
            weight={FontWeight.bold}
            size={FontSize.large}
            color={FontColor.black}
            align={FontAlign.between}
          />
          <div className="overview_purchaseDetails">
            <ITExplanationOverviewRow
              label={`不動産価格相当額`}
              value={formatPriceWithFallback(multiplyNullable(props.realEstatePriceEquivalent, amount))}
              size={FontSize.medium}
              color={FontColor.black}
              align={FontAlign.between}
              weight={FontWeight.normal}
            />
            <ITExplanationOverviewRow
              label={''}
              value={`（うち消費税等${formatPriceWithFallback(multiplyNullable(props.consumptionTaxAmount, amount))}）`}
              color={FontColor.gray}
              size={FontSize.small}
              align={FontAlign.between}
              weight={FontWeight.normal}
            />
            <ITExplanationOverviewRow
              label="修繕積立金相当額"
              value={formatPriceWithFallback(multiplyNullable(props.repairReserveFundEquivalent, amount))}
              size={FontSize.medium}
              color={FontColor.black}
              align={FontAlign.between}
              weight={FontWeight.normal}
            />
            <ITExplanationOverviewRow
              label="必要運転資金留保金相当額"
              value={formatPriceWithFallback(multiplyNullable(props.requiredWorkingCapitalReserveEquivalent, amount))}
              size={FontSize.medium}
              color={FontColor.black}
              align={FontAlign.between}
              weight={FontWeight.normal}
            />
          </div>
        </div>
        <LotInputCommonDescriptions />
        {props.showNotice && (
          <div className="lotInput_body_notice">
            {
              '契約締結日に75歳以上であるお客様は、原則としてオンライン取引サービスでのご契約はできません。恐れ入りますが、該当されるお客様はお取引店までご連絡ください。'
            }
          </div>
        )}
      </div>
    </div>
  );
};
